import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiX } from 'react-icons/fi';
import './WebinarModal.css';

const UserModal = ({ open, onClose, user }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'manager',
    company: '',
  });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get('/api/companies/').then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data?.results || [];
      setCompanies(list);
    }).catch(() => setCompanies([]));
    axios.get('/api/companies/current/').then((r) => {
      const defaultCompanyId = r.data?.id ? String(r.data.id) : '';
      if (user) {
        setFormData({
          email: user.email || '',
          password: '',
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          role: user.role || 'manager',
          company: user.company ? String(user.company) : '',
        });
      } else {
        setFormData({
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          role: 'manager',
          company: defaultCompanyId,
        });
      }
    }).catch(() => {
      if (user) {
        setFormData({
          email: user.email || '',
          password: '',
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          role: user.role || 'manager',
          company: user.company ? String(user.company) : '',
        });
      }
    });
  }, [user, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = { ...formData };
      if (!data.password) {
        delete data.password;
      }
      if (data.role === 'moderator') {
        delete data.company;
      } else if (data.company) {
        data.company = Number(data.company);
      } else {
        delete data.company;
      }

      if (user) {
        await axios.patch(`/api/users/${user.id}/`, data);
        toast.success(data.password ? 'Пользователь и пароль обновлены' : 'Пользователь обновлен');
      } else {
        await axios.post('/api/users/', data);
        toast.success('Пользователь создан');
      }
      // Очищаем форму после успешного сохранения
      setFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'manager',
      });
      onClose();
    } catch (error) {
      const payload = error.response?.data;
      let message = 'Ошибка сохранения';
      if (typeof payload === 'string') {
        message = payload;
      } else if (payload?.error) {
        message = payload.error;
      } else if (payload && typeof payload === 'object') {
        const firstFieldError = Object.values(payload).find((v) => Array.isArray(v) && v.length);
        if (firstFieldError) {
          message = firstFieldError[0];
        }
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{user ? 'Редактировать пользователя' : 'Добавить пользователя'}</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input
              type="email"
              className="form-input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Пароль {!user && '*'}</label>
            <input
              type="password"
              className="form-input"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!user}
              placeholder={user ? 'Оставьте пустым, чтобы не менять' : ''}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Имя</label>
            <input
              type="text"
              className="form-input"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Фамилия</label>
            <input
              type="text"
              className="form-input"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Роль *</label>
            <select
              className="form-select"
              value={formData.role}
              onChange={(e) => {
                const role = e.target.value;
                setFormData({
                  ...formData,
                  role,
                  company: role === 'moderator' ? '' : formData.company,
                });
              }}
              required
            >
              <option value="manager">Менеджер</option>
              <option value="moderator">Модератор</option>
            </select>
          </div>

          {(formData.role === 'manager' || formData.role === 'client') && companies.length > 0 && (
            <div className="form-group">
              <label className="form-label">Фирма *</label>
              <select
                className="form-select"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                required
              >
                <option value="">Выберите фирму</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;
